import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { uploadService } from '@/features/admin/services/uploadService';
import { toast } from 'sonner';
import { 
  Phone, 
  RefreshCcw, 
  Power,
  Edit,
  Save,
  X,
  LifeBuoy,
  Info,
  VolumeX,
  Grab
} from 'lucide-react';

// Custom "Double/x2" Icon for "جاوب جوابين" - Stylized number 2 with circle
const DoubleTwoIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
    style={style}
  >
    {/* Outer circle */}
    <circle cx="12" cy="12" r="10" strokeWidth="2" />
    {/* Stylized "2" number */}
    <path 
      d="M9 9.5C9 8.12 10.12 7 11.5 7H12.5C13.88 7 15 8.12 15 9.5C15 10.5 14.5 11.3 13.5 12L9 16H15" 
      strokeWidth="2.5"
      fill="none"
    />
  </svg>
);
import { apiClient, getFullImageUrl } from '@/lib/api/apiClient';

interface LifelineDto {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  iconUrl?: string;
  iconColor?: string;
  usageTime: string;
  effect: string;
  isEnabled: boolean;
  displayOrder: number;
}

interface UpdateLifelineRequest {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  iconUrl?: string;
  iconColor?: string;
  usageTime: string;
  effect: string;
  displayOrder: number;
}

// Icon mapping based on lifeline code
const getLifelineIcon = (code: string, color: string) => {
  const iconClass = `w-14 h-14`;
  const style = { color };
  
  switch (code) {
    case 'STEAL_QUESTION':
      return <Grab className={iconClass} style={style} />;
    case 'THE_PIT':
      return <RefreshCcw className={iconClass} style={style} />;
    case 'DOUBLE_ANSWER':
      return <DoubleTwoIcon className={iconClass} style={style} />;
    case 'REST':
    case 'SILENCE':
      return <VolumeX className={iconClass} style={style} />;
    case 'CALL_FRIEND':
      return <Phone className={iconClass} style={style} />;
    default:
      return <LifeBuoy className={iconClass} style={style} />;
  }
};

export const LifelinesPage = () => {
  const [lifelines, setLifelines] = useState<LifelineDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UpdateLifelineRequest | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLifelines();
  }, []);

  const fetchLifelines = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/admin/lifelines');
      console.log('Lifelines API response:', response.data);
      
      if (response.data?.data?.lifelines) {
        setLifelines(response.data.data.lifelines);
      } else if (response.data?.lifelines) {
        setLifelines(response.data.lifelines);
      } else if (Array.isArray(response.data?.data)) {
        setLifelines(response.data.data);
      } else if (Array.isArray(response.data)) {
        setLifelines(response.data);
      } else {
        console.error('Unexpected response format:', response.data);
        setLifelines([]);
      }
    } catch (error: any) {
      console.error('Lifelines API error:', error);
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'فشل تحميل وسائل المساعدة';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const response = await apiClient.patch(`/api/admin/lifelines/${id}/toggle`);
      const newStatus = response.data.isEnabled;
      setLifelines(prev => 
        prev.map(l => l.id === id ? { ...l, isEnabled: newStatus } : l)
      );
      toast.success(newStatus ? 'تم تفعيل وسيلة المساعدة' : 'تم تعطيل وسيلة المساعدة');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تغيير الحالة');
    }
  };

  const handleEdit = (lifeline: LifelineDto) => {
    setEditingId(lifeline.id);
    setEditForm({
      id: lifeline.id,
      name: lifeline.name,
      nameAr: lifeline.nameAr,
      description: lifeline.description,
      descriptionAr: lifeline.descriptionAr,
      iconUrl: lifeline.iconUrl,
      iconColor: lifeline.iconColor || '#000000',
      usageTime: lifeline.usageTime,
      effect: lifeline.effect,
      displayOrder: lifeline.displayOrder,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleSave = async () => {
    if (!editForm) return;

    try {
      setSaving(true);
      await apiClient.put(`/api/admin/lifelines/${editForm.id}`, editForm);
      setLifelines(prev =>
        prev.map(l => l.id === editForm.id ? { ...l, ...editForm } : l)
      );
      toast.success('تم حفظ التغييرات');
      handleCancelEdit();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل حفظ التغييرات');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    return await uploadService.uploadImage(file, 'lifelines');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-accent-greenLight/20 rounded-lg">
          <LifeBuoy className="w-8 h-8 text-accent-greenDark" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-1">وسائل المساعدة</h1>
          <p className="text-accent-dark/70">إدارة وسائل المساعدة المتاحة للاعبين أثناء اللعب</p>
        </div>
      </div>

      {/* Lifelines Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {lifelines.map((lifeline) => (
          <Card 
            key={lifeline.id} 
            className={`border-2 transition-all duration-300 overflow-hidden ${
              lifeline.isEnabled 
                ? 'border-accent-greenLight/30 bg-white shadow-md' 
                : 'border-gray-200 bg-gray-50/50 opacity-75'
            }`}
          >
            {/* Card Header with Colored Bar */}
            <div 
              className="h-2"
              style={{ backgroundColor: lifeline.iconColor || '#ccc' }}
            />
            
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <CardTitle className="text-2xl font-bold text-accent-dark">
                  {lifeline.nameAr}
                </CardTitle>
                <div className="flex gap-1">
                  {editingId !== lifeline.id && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(lifeline)}
                        className="text-blue-600 hover:bg-blue-50"
                        title="تعديل"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleStatus(lifeline.id)}
                        className={lifeline.isEnabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}
                        title={lifeline.isEnabled ? 'تعطيل' : 'تفعيل'}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {!lifeline.isEnabled && (
                <span className="inline-block px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded mt-1">
                  معطلة
                </span>
              )}
            </CardHeader>

            <CardContent>
              {editingId === lifeline.id && editForm ? (
                // Edit Mode
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-dark mb-1">
                        الاسم (عربي)
                      </label>
                      <Input
                        value={editForm.nameAr}
                        onChange={(e) => setEditForm({ ...editForm, nameAr: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-dark mb-1">
                        الاسم (إنجليزي)
                      </label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-accent-dark mb-1">
                      الوصف (عربي)
                    </label>
                    <textarea
                      value={editForm.descriptionAr}
                      onChange={(e) => setEditForm({ ...editForm, descriptionAr: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-secondary text-accent-dark"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-accent-dark mb-1">
                        لون الأيقونة
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={editForm.iconColor || '#000000'}
                          onChange={(e) => setEditForm({ ...editForm, iconColor: e.target.value })}
                          className="w-12 h-10 rounded border cursor-pointer"
                        />
                        <Input
                          value={editForm.iconColor || ''}
                          onChange={(e) => setEditForm({ ...editForm, iconColor: e.target.value })}
                          placeholder="#000000"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-accent-dark mb-1">
                        ترتيب العرض
                      </label>
                      <Input
                        type="number"
                        value={editForm.displayOrder}
                        onChange={(e) => setEditForm({ ...editForm, displayOrder: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-accent-dark mb-1">
                      وقت الاستخدام
                    </label>
                    <select
                      value={editForm.usageTime}
                      onChange={(e) => setEditForm({ ...editForm, usageTime: e.target.value })}
                      className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-secondary text-accent-dark"
                    >
                      <option value="BEFORE_QUESTION">قبل رؤية السؤال</option>
                      <option value="AFTER_QUESTION">بعد رؤية السؤال</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-accent-dark mb-1">
                      صورة مخصصة (اختياري)
                    </label>
                    <ImageUpload
                      value={editForm.iconUrl}
                      onChange={(url) => setEditForm({ ...editForm, iconUrl: url || undefined })}
                      onUpload={handleImageUpload}
                      placeholder="ارفع صورة للوسيلة"
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 bg-secondary hover:bg-secondary-dark text-white"
                    >
                      {saving ? <LoadingSpinner size="sm" /> : <><Save className="w-4 h-4 ml-2" /> حفظ</>}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                      className="flex-1"
                    >
                      <X className="w-4 h-4 ml-2" /> إلغاء
                    </Button>
                  </div>
                </div>
              ) : (
                // Display Mode
                <div className="flex items-start gap-5">
                  {/* Icon */}
                  <div 
                    className="flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ 
                      backgroundColor: `${lifeline.iconColor}15`,
                      border: `3px solid ${lifeline.iconColor}50`
                    }}
                  >
                    {lifeline.iconUrl && getFullImageUrl(lifeline.iconUrl) ? (
                      <img 
                        src={getFullImageUrl(lifeline.iconUrl)} 
                        alt={lifeline.nameAr} 
                        className="w-12 h-12 object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      getLifelineIcon(lifeline.code, lifeline.iconColor || '#000')
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <p className="text-accent-dark/80 mb-4 leading-relaxed text-base">
                      {lifeline.descriptionAr}
                    </p>
                    
                    <span 
                      className={`inline-block px-4 py-2 text-sm rounded-lg font-medium ${
                        lifeline.usageTime === 'BEFORE_QUESTION'
                          ? 'bg-orange-100 text-orange-700 border border-orange-200'
                          : 'bg-blue-100 text-blue-700 border border-blue-200'
                      }`}
                    >
                      {lifeline.usageTime === 'BEFORE_QUESTION' 
                        ? 'تستخدموها قبل ما تشوف السؤال'
                        : 'تستخدموها بعد ما تشوف السؤال'
                      }
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* No lifelines message */}
      {lifelines.length === 0 && (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="py-12 text-center">
            <LifeBuoy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-500 mb-2">لا توجد وسائل مساعدة</h3>
            <p className="text-gray-400">قم بتشغيل سكريبت قاعدة البيانات لإضافة وسائل المساعدة الأساسية</p>
          </CardContent>
        </Card>
      )}

      {/* Info Section */}
      <Card className="border-2 border-blue-100 bg-blue-50/50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-blue-900 mb-2">ملاحظات مهمة</h3>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• وسائل المساعدة الأساسية الخمسة موجودة مسبقاً في النظام</li>
                <li>• يمكنك تعديل الأسماء والأوصاف وألوان الأيقونات ورفع صور مخصصة</li>
                <li>• يمكنك تفعيل أو تعطيل أي وسيلة حسب احتياجات اللعبة</li>
                <li>• كل فريق يمكنه اختيار 3 وسائل قبل بدء اللعبة</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
